// 検索条件 ⇔ URL クエリ文字列の相互変換(純関数)。
//
// 生成するクエリは常に「全パラメータ明示・固定順」の正規形にする。
// 同じ条件は必ず同じ文字列になるため、
//  ・URL 更新の要否が文字列比較だけで判定できる(replace ループの防止)
//  ・localStorage には正規形文字列をそのまま保存できる(復元も本 codec を通るので
//    検証・権限クランプのコードパスが URL と1本化される)
//
// 例: date=placed&from=2026-06-01&to=2026-08-31&status=all&dept=101&own=0
import {
  DATE_KIND_IDS,
  STATUS_IDS,
  SEARCH_SCREENS,
  getDefaultConditions,
  normalizeConditions,
} from "./orderSearchModel";

export const SEARCH_PARAM_KEYS = [
  "date",
  "from",
  "to",
  "status",
  "dept",
  "own",
  "num",
];

const pad2 = (value) => String(value).padStart(2, "0");
const formatYmd = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

// "YYYY-MM-DD" のみ受け付ける。2026-02-31 のような繰り上がりは不正扱い。
const parseYmd = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : null;
};

// URLSearchParams.toString() は "," を %2C にしてしまい読みにくいので手組みする。
// エンコードは書き込み側の encodeURIComponent の1回だけ。読み出し側は
// URLSearchParams.get() が1回デコードするので、ここで再デコードしてはいけない
// (二重デコードすると "%" を含む値が URIError で丸ごと落ちる)。
// 制約: 値自体に "," を含む ID は CSV 形式では表現できない(部署 did は数字文字列運用)。
const joinCsv = (values) => values.map(encodeURIComponent).join(",");
const splitCsv = (raw) =>
  raw
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);

const isAll = (ids, allIds) => allIds.every((id) => ids.includes(id));

// 条件 → 正規形クエリ("?" なし)。
// includeOrderNumber=false は localStorage 保存用(古い発注番号が残ると
// 次回開いたとき一覧が空になり原因が分かりにくいため保存しない)。
export const encodeSearchParams = (cond, { includeOrderNumber = true } = {}) => {
  const parts = [
    `date=${isAll(cond.dateKinds, DATE_KIND_IDS) ? "all" : joinCsv(cond.dateKinds)}`,
    `from=${formatYmd(cond.from)}`,
    `to=${formatYmd(cond.to)}`,
    `status=${isAll(cond.statuses, STATUS_IDS) ? "all" : joinCsv(cond.statuses)}`,
    `dept=${cond.departmentIds === null ? "all" : joinCsv(cond.departmentIds)}`,
    `own=${cond.myOwn ? "1" : "0"}`,
  ];
  if (includeOrderNumber && cond.orderNumber) {
    parts.push(`num=${encodeURIComponent(cond.orderNumber)}`);
  }
  return parts.join("&");
};

// クエリ文字列("?" 付き/なし両対応) → 条件。
//  ・認識できるパラメータが1つも無い場合: hasParams=false + ロール既定値
//  ・一部だけある場合: 欠けたパラメータはロール既定値で補完
//    (localStorage は使わない。共有リンクの見え方が受け手の保存値に依存すると、
//     送り手と受け手で別の一覧が表示されてしまうため)
//  ・不正なトークンは無視し、結果が空になった項目はロール既定値
export const decodeSearchParams = (search, { screen, userInfo, now = new Date() }) => {
  const raw =
    typeof search === "string"
      ? search.startsWith("?")
        ? search.slice(1)
        : search
      : "";
  const params = new URLSearchParams(raw);
  // num は履歴画面だけの機能。処理画面では「?num=...だけのURL」を
  // パラメータ有りと数えない(localStorage 復元が抑止されてしまうため)
  const relevantKeys =
    screen === SEARCH_SCREENS.HISTORY
      ? SEARCH_PARAM_KEYS
      : SEARCH_PARAM_KEYS.filter((key) => key !== "num");
  const hasParams = relevantKeys.some((key) => params.has(key));
  const defaults = getDefaultConditions(screen, userInfo, now);
  if (!hasParams) {
    return { conditions: defaults, hasParams: false };
  }

  const readIdList = (key, allIds, defaultIds) => {
    const value = params.get(key);
    if (value === null) return defaultIds;
    if (value === "all") return [...allIds];
    const ids = splitCsv(value).filter((id) => allIds.includes(id));
    return ids.length > 0 ? ids : defaultIds;
  };

  const readDepartments = () => {
    const value = params.get("dept");
    if (value === null) return defaults.departmentIds;
    if (value === "all") return null;
    const ids = splitCsv(value);
    // 部署マスタとの突き合わせはしない(マスタは非同期ロードのため)。
    // 実在しない id は絞り込みで0件マッチするだけで害はない。
    return ids.length > 0 ? ids : defaults.departmentIds;
  };

  const conditions = normalizeConditions(
    userInfo,
    {
      dateKinds: readIdList("date", DATE_KIND_IDS, defaults.dateKinds),
      from: parseYmd(params.get("from")) ?? defaults.from,
      to: parseYmd(params.get("to")) ?? defaults.to,
      statuses: readIdList("status", STATUS_IDS, defaults.statuses),
      departmentIds: readDepartments(),
      // "1"/"0" 以外の不正トークンは他の項目と同様に既定値へ倒す
      myOwn:
        params.get("own") === "1"
          ? true
          : params.get("own") === "0"
          ? false
          : defaults.myOwn,
      // 発注番号は履歴画面だけの機能(処理画面では常に空)
      orderNumber:
        screen === SEARCH_SCREENS.HISTORY ? params.get("num") ?? "" : "",
    },
    now
  );
  return { conditions, hasParams: true };
};
